// Form-element property model — every builder element is
// `{ elementType, properties, collapsed }`, with `properties` instantiated from
// the per-type defaults below (mirrors the existing app's
// formElementsDefaultProperties, trimmed to the types this builder offers:
// note/status/audio/externalApi and the supervisor_* family are out of scope).
// `image` is a display-image element (basic + layout groups only). Colors are
// 8-digit hex (#RRGGBBAA). `fieldUniqueId` is stamped at creation time;
// `dbColumnIdentifier` was dropped from the model on purpose.

/** One answer choice of a checkList-style field. `itemColor` is 8-digit hex
 *  (#rrggbbaa; alpha 00 = no color). `value` is the option's scoring value. */
export interface ChoiceOption {
  responseText: string;
  value: string;
  itemColor: string;
}

/** A fresh answer choice (alpha-00 color = "no color" yet). `responseText` is a
 *  literal default, not an i18n key — it is model DATA that persists into the
 *  saved design and is answered against in the field, so it must not shift with
 *  the admin's UI language (same idiom as ratingSlider's 'Bad'/'Good' labels;
 *  this module also stays import-free by design). */
export const emptyChoiceOption = (): ChoiceOption => ({
  responseText: 'Option',
  value: '0',
  itemColor: '#00000000',
});

/** One entry of a lookup field's `bindingList` — used by **both** `dataSource`
 *  and `companyField`: a form field wired to a column of the picked source, so a
 *  lookup can fill it in. `fieldId` is the bound element's `elementGlobalId`, or
 *  its `uniqueIdToReplace` while it has never been saved; `columnId` comes from
 *  the source's column schema.
 *  NOTE: the key names are inferred from the design — not yet backend-confirmed. */
export interface DataSourceBinding {
  fieldId: string;
  columnId: string;
}

/** An image field's reference image, as stored in `basicProperties.fieldImage`.
 *  `id` is the uploaded media id — the only part that identifies the image;
 *  `formId`/`versionId` record where it was uploaded. No display URL lives here:
 *  it is resolved at render time and held in component state (useFieldImageUrl),
 *  so the whole descriptor persists as-is. */
export interface FieldImageRef {
  formId?: string;
  versionId?: string;
  id?: string;
}

const pageBasicProperties = {
  pageTitleColor: '#000000ff',
  pageTitleSize: '16px',
  pageTitleFontType: [] as string[],
  pageTitle: '',
};

const sectionBasicProperties = {
  sectionTitleColor: '#ffffffff',
  sectionTitleSize: '14px',
  sectionTitleFontType: [] as string[],
  sectionHintTextColor: '#000000ff',
  sectionHintTextSize: '12px',
  sectionHintTextFontType: [] as string[],
  sectionTitle: '',
  sectionHintText: '',
};

const fieldBasicProperties = {
  fieldTitleColor: '#000000ff',
  fieldTitleSize: '14px',
  fieldTitleFontType: [] as string[],
  fieldHintTextColor: '#000000ff',
  fieldHintTextSize: '12px',
  fieldHintTextFontType: [] as string[],
  fieldTitle: '',
  fieldHintText: '',
};

const otherProperties = {
  fieldUniqueId: '',
  defaultValue: '',
  shortTitle: '',
  isPersonalData: false,
};

// Exported: formExportJson's sanitizer resets watermark keys to these defaults
// across every type that carries mediaProperties, so it reads the shared base
// rather than one arbitrary type's copy.
export const mediaProperties = {
  allowNotes: false,
  allowPictures: false,
  requiredPictures: false,
  allowPictureUpload: false,
  allowVideoUpload: false,
  allowVideos: false,
  allowDocuments: false,
  showTimeStamp: false,
  showTitle: false,
  restrictCameraOrientation: false,
  allowDownload: false,
  minPictures: 1,
  maxPictures: 3,
  videoMinutes: 0,
  videoSeconds: 5,
  cameraOrientation: 'vertical',
  enableWatermark: false,
  watermarkText: '',
  watermarkAlignment: 'bottom',
  watermarkTextCenter: false,
  watermarkUsedFieldsMap: {} as Record<string, string>,
};

const layoutProperties = {
  backgroundColor: '#ffffffff',
  transparent: false,
};

const ticketProperties = {
  ticketEnabled: false,
  ticketTitle: '',
  ticketDefaultType: null as string | null,
  allowTicketTypeSelection: true,
};

export const formElementDefaultProperties = {
  page: {
    basicProperties: { ...pageBasicProperties },
    validationProperties: { validateRequiredFields: true },
    layoutProperties: { ...layoutProperties },
  },
  text: {
    basicProperties: { ...fieldBasicProperties, fieldPlaceholder: '' },
    ticketProperties: { ...ticketProperties },
    validationProperties: {
      required: false,
      readOnly: false,
      hidden: false,
      hideOnPdf: false,
      restriction: false,
      minRange: 1,
      maxRange: 50,
    },
    layoutProperties: {
      ...layoutProperties,
      textLines: 'single',
      answerFormat: 'description',
      fieldLayout: 'horizontal',
      keyboardStyle: 'keyboard',
    },
    otherProperties: { ...otherProperties },
    mediaProperties: { ...mediaProperties },
  },
  number: {
    basicProperties: { ...fieldBasicProperties, fieldPlaceholder: '', allowEnglishNumbers: false },
    ticketProperties: { ...ticketProperties },
    validationProperties: {
      required: false,
      readOnly: false,
      hidden: false,
      hideOnPdf: false,
      enableScoring: true,
      restriction: false,
      minRange: 1,
      maxRange: 10,
    },
    layoutProperties: {
      ...layoutProperties,
      numberType: 'integer',
      fieldLayout: 'horizontal',
      keyboardStyle: 'keyboard',
    },
    otherProperties: { ...otherProperties },
    mediaProperties: { ...mediaProperties },
  },
  checkList: {
    basicProperties: {
      ...fieldBasicProperties,
      subType: 'choiceList',
      multipleChoice: false,
      optionList: [] as ChoiceOption[],
    },
    ticketProperties: { ...ticketProperties },
    layoutProperties: { ...layoutProperties, fieldLayout: 'horizontal', collapseOnSelect: false },
    validationProperties: {
      required: false,
      hidden: false,
      hideOnPdf: false,
      enableScoring: true,
    },
    otherProperties: { ...otherProperties },
    mediaProperties: { ...mediaProperties },
  },
  dateTime: {
    basicProperties: { ...fieldBasicProperties, subType: 'date', showHijriDate: false },
    ticketProperties: { ...ticketProperties },
    validationProperties: {
      required: false,
      readOnly: false,
      hidden: false,
      hideOnPdf: false,
      restriction: false,
      minDate: '',
      maxDate: '',
    },
    layoutProperties: { ...layoutProperties, fieldLayout: 'horizontal', keyboardStyle: 'keyboard' },
    otherProperties: { ...otherProperties },
    mediaProperties: { ...mediaProperties },
  },
  ratingSlider: {
    basicProperties: {
      ...fieldBasicProperties,
      lowLabel: 'Bad',
      highLabel: 'Good',
      ratingPointers: 5,
      ratingIcon: 'star',
    },
    ticketProperties: { ...ticketProperties },
    validationProperties: {
      required: false,
      readOnly: false,
      hidden: false,
      hideOnPdf: false,
      enableScoring: true,
    },
    layoutProperties: { ...layoutProperties },
    otherProperties: { ...otherProperties },
    mediaProperties: { ...mediaProperties },
  },
  numberSlider: {
    basicProperties: { ...fieldBasicProperties, minRange: 1, maxRange: 10 },
    ticketProperties: { ...ticketProperties },
    validationProperties: {
      required: false,
      readOnly: false,
      hidden: false,
      hideOnPdf: false,
      enableScoring: true,
    },
    layoutProperties: { ...layoutProperties },
    otherProperties: { ...otherProperties },
    mediaProperties: { ...mediaProperties },
  },
  image: {
    basicProperties: {
      ...fieldBasicProperties,
      // Client-only pending pick (a File until the next save uploads it).
      fieldImageFile: null as unknown,
      fieldImage: null as FieldImageRef | null,
    },
    layoutProperties: {
      ...layoutProperties,
      fieldLayout: 'horizontal',
      withoutFrame: false,
    },
  },
  signature: {
    basicProperties: { ...fieldBasicProperties },
    ticketProperties: { ...ticketProperties },
    validationProperties: { required: false, hidden: false, hideOnPdf: false },
    layoutProperties: { ...layoutProperties, signatureColor: '#000000ff' },
    otherProperties: { ...otherProperties },
    mediaProperties: { ...mediaProperties },
  },
  location: {
    basicProperties: { ...fieldBasicProperties },
    ticketProperties: { ...ticketProperties },
    validationProperties: {
      required: false,
      readOnly: false,
      hidden: false,
      hideOnPdf: false,
      showStamp: false,
      allowSelectLocation: false,
      allowShareLocation: false,
      geofence: false,
      geofenceRange: 5,
      geofenceRestricted: false,
      geofenceEnableDirection: false,
    },
    layoutProperties: {
      ...layoutProperties,
      mapType: 'standard',
      accuracy: '5',
      fieldLayout: 'horizontal',
      keyboardStyle: 'keyboard',
    },
    otherProperties: { ...otherProperties },
    mediaProperties: { ...mediaProperties },
  },
  qrCode: {
    basicProperties: { ...fieldBasicProperties, fieldPlaceholder: '' },
    ticketProperties: { ...ticketProperties },
    validationProperties: { required: false, hidden: false, hideOnPdf: false },
    layoutProperties: { ...layoutProperties, textLines: 'single', fieldLayout: 'horizontal' },
    otherProperties: { ...otherProperties },
    mediaProperties: { ...mediaProperties },
  },
  otp: {
    basicProperties: {
      ...fieldBasicProperties,
      otpSendVia: 'sms',
      countrySelectionMethod: 'by_ip_location',
      fieldPlaceholder: '',
      otpMessageContent: '',
      otpMessageContentMap: {} as Record<string, string>,
    },
    ticketProperties: { ...ticketProperties },
    validationProperties: {
      required: false,
      readOnly: false,
      restrictToSubmit: false,
      hidden: false,
      hideOnPdf: false,
    },
    layoutProperties: { ...layoutProperties, fieldLayout: 'horizontal' },
    otherProperties: { ...otherProperties },
    mediaProperties: { ...mediaProperties },
  },
  dataSource: {
    basicProperties: {
      ...fieldBasicProperties,
      subType: 'dropdown',
      fieldPlaceholder: '',
      dataType: 'text',
      dataSourceType: 'internal',
      dataSource: '',
      displayColumn: '',
      valueColumn: '',
      bindingList: [] as DataSourceBinding[],
    },
    ticketProperties: { ...ticketProperties },
    layoutProperties: { ...layoutProperties, fieldLayout: 'horizontal', collapseOnSelect: false },
    validationProperties: {
      required: false,
      showDataStatus: true,
      hidden: false,
      hideOnPdf: false,
    },
    otherProperties: { ...otherProperties },
    mediaProperties: { ...mediaProperties },
  },
  sendTo: {
    basicProperties: { ...fieldBasicProperties, sendToType: 'email' },
    ticketProperties: { ...ticketProperties },
    validationProperties: {
      required: false,
      readOnly: false,
      hidden: false,
      hideOnPdf: false,
      sendAfterSubmit: false,
      sendAfterApproval: false,
    },
    layoutProperties: { ...layoutProperties },
    otherProperties: { ...otherProperties },
    mediaProperties: { ...mediaProperties },
  },
  // Same group set as companyField, but only the plain field basics.
  companyStructure: {
    basicProperties: { ...fieldBasicProperties },
    ticketProperties: { ...ticketProperties },
    layoutProperties: { ...layoutProperties, fieldLayout: 'horizontal', collapseOnSelect: false },
    // `required` defaults ON for this type — a submission has to say which node
    // of the company structure it belongs to.
    validationProperties: { required: true, hidden: false, hideOnPdf: false },
    otherProperties: { ...otherProperties },
    mediaProperties: { ...mediaProperties },
  },
  companyField: {
    basicProperties: {
      ...fieldBasicProperties,
      subType: 'dropdown',
      companyFieldId: '',
      companyFieldDisplayColumns: [] as string[],
      bindingList: [] as DataSourceBinding[],
    },
    ticketProperties: { ...ticketProperties },
    layoutProperties: { ...layoutProperties, fieldLayout: 'horizontal', collapseOnSelect: false },
    validationProperties: { required: false, hidden: false, hideOnPdf: false },
    otherProperties: { ...otherProperties },
    mediaProperties: { ...mediaProperties },
  },
  section: {
    basicProperties: {
      ...sectionBasicProperties,
      sectionRepeated: false,
      sectionMinRepetition: 0,
      sectionMaxRepetition: 0,
    },
    validationProperties: { hidden: false, hideOnPdf: false },
    layoutProperties: { ...layoutProperties, backgroundColor: '#38cb89ff' },
  },
};

export type ElementType = keyof typeof formElementDefaultProperties;
export type ElementProperties = (typeof formElementDefaultProperties)[ElementType];
export type PageProperties = (typeof formElementDefaultProperties)['page'];
/** Element types offered by the field library (everything except `page`). */
export type LibraryElementType = Exclude<ElementType, 'page'>;

export const newFieldUniqueId = () => `field_${Date.now()}`;

/** Key prefix inside basicProperties — sections name their keys sectionTitle/
 *  sectionHintText…, pages pageTitle… (no hint), everything else fieldTitle/
 *  fieldHintText…. */
export const basicKeyPrefix = (type: ElementType): 'section' | 'page' | 'field' =>
  type === 'section' ? 'section' : type === 'page' ? 'page' : 'field';

/** basicProperties as a loose record for prefix-computed key access (the union
 *  isn't discriminated by elementType, so common UI reads/writes go through
 *  this; writes must preserve the object shape). */
export const basicRecord = (props: ElementProperties): Record<string, unknown> =>
  props.basicProperties as unknown as Record<string, unknown>;

/** Fresh, independent properties for a new element — a deep clone (the defaults
 *  share nested refs like optionList/watermarkUsedFieldsMap) with the
 *  fieldUniqueId stamped where the type carries otherProperties. */
export const createElementProperties = <T extends ElementType>(
  type: T,
): (typeof formElementDefaultProperties)[T] => {
  const props = structuredClone(formElementDefaultProperties[type]);
  if ('otherProperties' in props) props.otherProperties.fieldUniqueId = newFieldUniqueId();
  return props;
};
